import fetcher, { FleetPaginated, FleetQueryParams } from '../fetcher';
import { MasterData } from './master-data';
import { FileData, FilePayload } from './types';

export type CustomerList = {
	no: number;
	customerId: string;
	customerName: string;
	npwpNo: string;
	prospects: {
		prospectId: string;
		prospectStatus: string;
	}[];
};

// OCR List (Customer)
export const getCustomerList = (params?: FleetQueryParams) => {
	return fetcher<FleetPaginated<CustomerList>>({
		api: '/ocrs/get-customer-list',
		body: params,
	});
};

// OCR - Detail - Session List
export type OCRSession = {
	id: number;
	sessionId: string;
	sessionName: string;
	status: MasterData;
};

export type OCRSessionList = {
	ocrList: OCRSession[];
	banks: MasterData[];
};

export type OCRSessionFile = {
	ocrId: number;
	sessionId: string;
	file: FileData;
};

export const downloadFile = (ocrId: number) => {
	return fetcher<OCRSessionFile>({
		api: '/ocrs/download-excel-result',
		body: {
			ocrId,
		},
	});
};

export const getDetail = (customerId: string) => {
	return fetcher<OCRSessionList>({
		api: '/ocrs/get-detail',
		body: {
			customerId,
		},
	});
};

export type OCRSessionResult = {
	ocrId: number;
	sessionId: string;
	result: string;
};

export const getSessionResult = (ocrId: number) => {
	return fetcher<OCRSessionResult>({
		api: '/ocrs/get-result',
		body: {
			ocrId,
		},
	});
};

// OCR - Detail - Generate OCR
export type BankDocument = {
	bankId: string;
	documents?: FilePayload[];
};

export type OCRGeneratePayload = {
	customerId: string;
	sessionName: string;
	documents: BankDocument[];
};

export const generateOCR = (body: OCRGeneratePayload) => {
	return fetcher<OCRSessionList>({
		api: '/ocrs/generate-ocr',
		body,
	});
};

// OCR - Detail - Session - Document List
export type OCRBankStatement = {
	bankName: string;
	passStatements: string[];
	failStatements: string[];
	remainingStatements: string[];
};

export type OCRSessionDocumentList = {
	bankStatements: OCRBankStatement[];
};

export type OCRSessionDocumentListNormalized = {
	bankName: string;
	fileName: string;
	status: string;
};

export const getSessionDocumentList = (ocrId: number) => {
	return fetcher<OCRSessionDocumentList>({
		api: '/ocrs/get-document-list',
		body: {
			ocrId,
		},
	});
};

// OCR Detail - Session - Download File Result
export const downloadExcelResult = (ocrId: number) => {
	return fetcher<OCRSessionList>({
		api: '/ocrs/download-excel-result',
		body: {
			ocrId,
		},
	});
};

// OCR Detail - Session - Detail - Results
export type OCRSessionDetail = {
	sessionId: string;
	sessionName: string;
	analysisResult: string;
};

export const getSessionDetail = (sessionId: string) => {
	return fetcher<OCRSessionDetail>({
		api: '/ocrs/get-session-detail',
		body: {
			sessionId,
		},
	});
};

export type OCRAnalysisResultAccount = {
	accountHolderName: string;
	accountNumber: string;
	address: string;
	bankName: string;
	currency: string;
	period: string;
	type: string;
};

export type rawAccountData = {
	'Account Holder name': string;
	'Account Number': string;
	'Address': string;
	'Bank Name': string;
	'Currency': string;
	'Period': string;
	'Type': string;
};

export function mapRawToOCRAccount(
	raw: rawAccountData,
): OCRAnalysisResultAccount {
	return {
		accountHolderName: raw['Account Holder name'],
		accountNumber: raw['Account Number'],
		address: raw['Address'],
		bankName: raw['Bank Name'],
		currency: raw['Currency'],
		period: raw['Period'],
		type: raw['Type'],
	};
}

type RawOCRData = {
	'index': string;
	'month': string;
	'Ending balances': number;
	'Starting balances': number;
	'average_EOD_balance': number;
	'max_EOD_balance': number;
	'min_EOD_balance': number;
	'total_amount_of_cash_deposits': number;
	'total_amount_of_cash_withdrawals': number;
	'total_amount_of_credit_transactions': number;
	'total_amount_of_debit_transactions': number;
	'total_credit_transactions': number;
	'total_debit_transactions': number;
	'total_number_of_cash_deposits': number;
	'total_number_of_cash_withdrawals': number;
};

export type OCRAnalysisResult = {
	index: string;
	month: string;
	total_amount_of_credit_transactions: number;
	total_credit_transactions: number;
	total_amount_of_debit_transactions: number;
	total_debit_transactions: number;
	total_amount_of_cash_deposits: number;
	total_number_of_cash_deposits: number;
	total_amount_of_cash_withdrawals: number;
	total_number_of_cash_withdrawals: number;
	average_EOD_balance: number;
	min_EOD_balance: number;
	max_EOD_balance: number;
	starting_balances: number;
	ending_balances: number;
};

export function mapRawToOCRAnalysisResult(raw: RawOCRData): OCRAnalysisResult {
	return {
		index: raw['index'],
		month: raw['month'],
		total_amount_of_credit_transactions:
			raw['total_amount_of_credit_transactions'],
		total_credit_transactions: raw['total_credit_transactions'],
		total_amount_of_debit_transactions:
			raw['total_amount_of_debit_transactions'],
		total_debit_transactions: raw['total_debit_transactions'],
		total_amount_of_cash_deposits: raw['total_amount_of_cash_deposits'],
		total_number_of_cash_deposits: raw['total_number_of_cash_deposits'],
		total_amount_of_cash_withdrawals: raw['total_amount_of_cash_withdrawals'],
		total_number_of_cash_withdrawals: raw['total_number_of_cash_withdrawals'],
		average_EOD_balance: raw['average_EOD_balance'],
		min_EOD_balance: raw['min_EOD_balance'],
		max_EOD_balance: raw['max_EOD_balance'],
		starting_balances: raw['Starting balances'],
		ending_balances: raw['Ending balances'],
	};
}

export type OCRAnalysisResultFraudIndicator = {
	countOfTriggers: number;
	fraudTag: string;
	fraudIndicator: string;
	identified: string;
	remarks: string;
};

type rawFraudIndicatorData = {
	'Count of Triggers': number;
	'Fraud Tag': string;
	'Fraud indicator': string;
	'Identified': string;
	'Remarks': string;
};

export function mapRawToOCRFraudIndicator(
	raw: rawFraudIndicatorData,
): OCRAnalysisResultFraudIndicator {
	return {
		countOfTriggers: raw['Count of Triggers'],
		fraudTag: raw['Fraud Tag'],
		fraudIndicator: raw['Fraud indicator'],
		identified: raw['Identified'],
		remarks: raw['Remarks'],
	};
}

// EOD Balance Sheets
export type EODBalanceSheet = {
	[month: string]: { [day: string]: number };
};
